import React, { useState, useEffect, useCallback } from 'react';
import { BookOpen, FileText, Eye, CheckCircle, Loader, ExternalLink } from 'lucide-react';

// "Delivery Procedures" — the driver's shelf of Australia Post delivery-standard
// reference material (illustrated Knock & Call Out guide, the DCA Operations
// Manual, and whatever the office adds next). Opening an item stamps a view
// receipt the office can see (who read what, and when) — same accountability the
// My Docs shelf gives per-driver documents, but this library is shared by all.

const API = '/.netlify/functions/delivery-procedures-sb';

function fmtWhen(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('en-AU', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
}

export default function DeliveryProceduresTab() {
  const [items, setItems] = useState(null);   // null = loading
  const [views, setViews] = useState({});
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState('');

  const api = useCallback(async (payload) => {
    const res = await fetch(API, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return res.json();
  }, []);

  const load = useCallback(async () => {
    try {
      const d = await api({ action: 'list' });
      if (d && d.success) { setItems(d.items || []); setViews(d.views || {}); }
      else { setItems([]); setError(d?.error || 'Could not load procedures'); }
    } catch (e) { setItems([]); setError(e.message); }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const onOpen = (item) => {
    // Open synchronously (before any await) so mobile popup blockers don't eat it.
    window.open(item.url, '_blank', 'noopener');
    setBusyId(item.id);
    // Stamp the view receipt in the background; refresh the "last viewed" line.
    api({ action: 'logView', id: item.id })
      .then(d => { if (d && d.success && d.view) setViews(v => ({ ...v, [item.id]: d.view })); })
      .catch(() => {})
      .finally(() => setBusyId(''));
  };

  if (items === null) {
    return <div style={{ padding: 24, textAlign: 'center', color: 'var(--hp-dim)' }}><Loader size={16} /> Loading procedures…</div>;
  }

  const cats = [...new Set(items.map(i => i.category || 'Delivery Standards'))];

  return (
    <div style={{ width: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <BookOpen size={18} color="var(--hp-gold)" />
        <span style={{ fontFamily: 'var(--font-display)', fontSize: '1.3rem', color: 'var(--hp-text)', letterSpacing: '0.03em' }}>Delivery Procedures</span>
      </div>
      <div style={{ fontSize: '0.8rem', color: 'var(--hp-dim)', marginBottom: 16 }}>
        The Australia Post delivery standards for our run. Please read each one — the office can see when you've opened it.
      </div>

      {error && <div style={{ color: 'var(--hp-red)', fontSize: '0.8rem', marginBottom: 12 }}>{error}</div>}

      {!items.length && (
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--hp-dim)', border: '1px dashed var(--hp-border)', borderRadius: 'var(--radius)' }}>
          Nothing published yet. Delivery procedures will appear here.
        </div>
      )}

      {cats.map(cat => {
        const group = items.filter(i => (i.category || 'Delivery Standards') === cat);
        if (!group.length) return null;
        return (
          <div key={cat} style={{ marginBottom: 22 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
              <span style={{ fontFamily: 'var(--font-body)', fontWeight: 700, fontSize: '0.78rem', letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--hp-dim)' }}>{cat}</span>
              <span style={{ flex: 1, height: 1, background: 'var(--hp-border)' }} />
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {group.map(item => {
                const v = views[item.id];
                const seen = !!v;
                return (
                  <div key={item.id} style={{
                    border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', padding: 14,
                    background: seen ? 'var(--hp-surface)' : 'rgba(201,168,76,0.06)',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                      {item.kind === 'guide'
                        ? <BookOpen size={18} color="var(--hp-gold)" style={{ flexShrink: 0, marginTop: 2 }} />
                        : <FileText size={18} color="var(--hp-gold)" style={{ flexShrink: 0, marginTop: 2 }} />}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 700, fontSize: '0.9rem', color: 'var(--hp-text)', display: 'flex', alignItems: 'center', gap: 8 }}>
                          {item.title}
                          {item.kind === 'guide' && (
                            <span style={{ fontSize: '0.62rem', fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--hp-gold)', border: '1px solid var(--hp-gold)', borderRadius: 999, padding: '1px 7px' }}>Illustrated</span>
                          )}
                        </div>
                        {item.subtitle && <div style={{ fontSize: '0.74rem', color: 'var(--hp-dim)', marginTop: 3, lineHeight: 1.45 }}>{item.subtitle}</div>}
                        <div style={{ fontSize: '0.7rem', marginTop: 5 }}>
                          {seen ? (
                            <span style={{ color: 'var(--hp-green)', display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                              <CheckCircle size={11} /> Last opened {fmtWhen(v.last)}{v.count > 1 ? ` · ${v.count}×` : ''}
                            </span>
                          ) : (
                            <span style={{ color: 'var(--hp-dim)' }}>Not opened yet</span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                      <button onClick={() => onOpen(item)} disabled={busyId === item.id}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '8px 14px', fontSize: '0.82rem', fontWeight: 800, borderRadius: 'var(--radius)', cursor: 'pointer', border: 'none', background: 'var(--hp-gold)', color: '#1a1a2e', opacity: busyId === item.id ? 0.6 : 1 }}>
                        {item.kind === 'guide' ? <Eye size={14} /> : <ExternalLink size={14} />} {busyId === item.id ? 'Opening…' : (seen ? 'Open again' : 'Open & read')}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
